// RITA-1522 (Nutzungserfassung Phase 3d, Epic RITA-1496) — Plaketten rückwärts zuordnen.
//
// Die Antwort liefert je Bedienelement nur einen SCHLÜSSEL und eine Zahl, keine Position: die
// Lese-API kennt das DOM nicht, und eine gespeicherte Position wäre mit der Uhrzeit ein
// Verhaltensdatum. Das Overlay muss den Weg der Erfassung deshalb rückwärts gehen.
//
// Der Weg ist bewusst DERSELBE Code: jedes Element aus INTERACTIVE_SELECTOR durch
// resolveClickTarget schicken und den erzeugten Schlüssel vergleichen (services/clickTarget.ts).
// Eine eigene Regel hier ('[data-usage="…"]' suchen) träfe markierte Elemente, aber keinen der
// technischen Schlüssel ('link:/dashboard', 'button:speichern') — und driftete bei der nächsten
// Änderung an der Erfassung still auseinander.
//
// Reine Funktion ohne React: sie liest das Dokument und gibt Paare zurück; das Positionieren und
// das Neuzeichnen bei scroll/resize macht die Komponente.
import { INTERACTIVE_SELECTOR, resolveClickTarget } from '../services/clickTarget';
import type { UsageClickMapElement } from '../types';

/** Eine Plakette: die Zahlen der Antwort und das Element, an dem sie hängt. */
export interface PlakettenTreffer {
  element: UsageClickMapElement;
  ziel: Element;
}

/**
 * Ordnet die Plaketten der Antwort den Bedienelementen des Dokuments zu.
 *
 * Mehrere Vorkommen desselben Schlüssels (eine Bearbeiten-Aktion je Tabellenzeile) bekommen JEDE
 * eine Plakette: die Zahl gilt für den Schlüssel über die ganze Seite, und welches Vorkommen
 * „das richtige“ wäre, weiß niemand. Schlüssel ohne Vorkommen entfallen still — die Karte gilt
 * für die ganze SEITE im Zeitraum, das DOM zeigt nur den aktuellen Zustand (Kopfleiste,
 * ausgeklappte Bereiche, gefilterte Tabellen).
 */
export const plakettenZuordnen = (elemente: readonly UsageClickMapElement[], doc: Document): PlakettenTreffer[] => {
  if (elemente.length === 0) return [];
  const gesucht = new Map<string, UsageClickMapElement>();
  elemente.forEach((element) => {
    if (!gesucht.has(element.key)) gesucht.set(element.key, element);
  });

  const treffer: PlakettenTreffer[] = [];
  doc.querySelectorAll(INTERACTIVE_SELECTOR).forEach((kandidat) => {
    const ziel = resolveClickTarget(kandidat);
    if (!ziel) return;
    const element = gesucht.get(ziel.key);
    if (element === undefined) return;
    treffer.push({ element, ziel: kandidat });
  });
  // Reihenfolge der Antwort (nach Klicks absteigend) beibehalten: die stärkste Plakette soll bei
  // Überdeckung oben liegen, und die Testerwartungen lesen sich danach.
  const rang = new Map<string, number>();
  elemente.forEach((element, stelle) => {
    if (!rang.has(element.key)) rang.set(element.key, stelle);
  });
  return treffer.sort((a, b) => (rang.get(a.element.key) ?? 0) - (rang.get(b.element.key) ?? 0));
};

export default plakettenZuordnen;
